import { type Ref, computed } from "@nuxtjs/composition-api";

import { useAmplitude } from "@/composables";
import { useCartsStore } from "@/store";
import { filterRemoveUndefined } from "@/utils/transform/array";

export const useCartItem = (retailerProductVariantId: Ref<string | undefined>) => {
  const cartsStore = useCartsStore();

  const cartItem = computed(() =>
    Object.values(cartsStore.cartItems).find(
      (cartItem) => cartItem?.retailerProductVariantId === retailerProductVariantId.value,
    ),
  );

  return { cartItem };
};

export const useCartsDrawerEvents = (cartId: Ref<string | null>) => {
  const amplitude = useAmplitude();
  const cartsStore = useCartsStore();

  const myCartsProps = computed<CartsViewMyCartsProps>(() => {
    const carts = Object.values(cartsStore.carts).filter(filterRemoveUndefined);
    const advertiserNames = carts
      .map((cart) => {
        const retailer = cartsStore.retailers[cart?.retailerId ?? ""];
        return retailer?.displayName;
      })
      .filter(filterRemoveUndefined);

    return {
      "number-of-carts": String(carts.length),
      "advertiser-names": advertiserNames,
    };
  });

  const singleMyCartProps = computed<CartsViewSingleCartsProps>(() => {
    const cart = cartsStore.carts[cartId.value ?? ""];
    const cartItems =
      cart?.cartItemIds.map((cartItemId) => cartsStore.cartItems[cartItemId]).filter(filterRemoveUndefined) ?? [];
    const retailerProductVariants = cartItems
      .map((cartItem) => cartsStore.retailerProductVariants[cartItem.retailerProductVariantId])
      .filter(filterRemoveUndefined);

    return {
      "advertiser-name": cartsStore.retailers[cart?.retailerId ?? ""]?.displayName ?? "retailer",
      "has-price-drop": retailerProductVariants.some(({ price, salePrice }) => salePrice && salePrice !== price),
      subtotal: cart?.subtotal?.toString() ?? "",
      "total-number-of-items": String(cartItems.reduce((prev, curr) => prev + curr.quantity, 0)),
      "total-number-of-unique-items": String(cartItems.length),
      "total-numer-of-unavailable-items": String(
        retailerProductVariants.reduce((prev, curr) => prev + (curr.availability ? 0 : 1), 0),
      ),
    };
  });

  const checkoutStartProps = computed<CartsCheckoutStartProps>(() => {
    const cart = cartsStore.carts[cartId.value ?? ""];
    const cartItems =
      cart?.cartItemIds.map((cartItemId) => cartsStore.cartItems[cartItemId]).filter(filterRemoveUndefined) ?? [];
    const retailerProductVariants = cartItems
      .map((cartItem) => cartsStore.retailerProductVariants[cartItem.retailerProductVariantId])
      .filter(filterRemoveUndefined);

    return {
      "advertiser-name": cartsStore.retailers[cart?.retailerId ?? ""]?.displayName ?? "retailer",
      "has-price-drop": retailerProductVariants.some(({ price, salePrice }) => salePrice && salePrice !== price),
      subtotal: cart?.subtotal?.toString() ?? "",
      "total-number-of-items": String(cartItems.reduce((prev, curr) => prev + curr.quantity, 0)),
      "total-number-of-unique-items": String(cartItems.length),
    };
  });

  const sendViewedMyCartsEvent = () => {
    amplitude?.sendEvent("/viewed-my-cart_page", myCartsProps.value);
  };

  const sendViewedSingleCartEvent = () => {
    amplitude?.sendEvent("/viewed-single-my-cart_page", singleMyCartProps.value);
  };

  const sendCheckoutStartEvent = () => {
    amplitude?.sendExitEvent("/cart-loaded/checkout-start", checkoutStartProps.value);
  };

  return { sendViewedMyCartsEvent, sendViewedSingleCartEvent, sendCheckoutStartEvent };
};
